import React, {
  ComponentProps,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { AutocompleteInputChangeReason } from '@mui/material';
import Img from 'react-cool-img';
import DeleteIcon from 'spw-static/images/delete-input-icon.svg';
import { colors } from 'spw-theme';
import { getIdentity } from 'spw-shared/utils';
import { Item } from '../../types';
import {
  Row,
  SPWBaseSelect,
  SPWCancelButton,
  SPWIconButton,
  SPWSaveButton,
  SPWSearchInput,
} from '.';
import { SPWFormDialog } from './dialogs';

const useStyles = makeStyles({
  form: {
    '& .description': {
      marginTop: '16px',
    },
  },
  searchInput: {
    width: '100%',
  },
  small: {
    display: 'block',
    fontSize: '12px',
    lineHeight: '16px',
    marginBottom: '18px',
    marginTop: '24px',
  },
  selectedItemsList: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    '& li:not(:last-child)': {
      marginBottom: '8px',
    },
  },
  saveButton: {
    marginRight: '16px',
  },
});

type ItemsSelectionDialogProps<T> = {
  className?: string;
  formDialogProps: Omit<
    ComponentProps<typeof SPWFormDialog>,
    'className' | 'open' | 'onClose' | 'disableScrollLock' | 'dialogActions'
  >;
  getTextOnTopOfSelectedList: (selectedItems: T[]) => string;
  isVisible: boolean;
  loading?: boolean;
  selectionComponent?: 'search-input' | 'select';
  onClose: () => void;
  onSave: (selectedItems: T[]) => void;
  saveButtonAriaLabel?: string;
  items: T[];
  preSelectedItems?: T[];
  onSelectedItemsChange?: (selectedItems: T[]) => void;
  searchInputProps?: Omit<
    ComponentProps<typeof SPWSearchInput>,
    'onChange' | 'onDelete' | 'options' | 'value'
  >;
  SelectedListItem: FC<{ item: T }>;
  selectProps?: Omit<
    ComponentProps<typeof SPWBaseSelect>,
    'fullWidth' | 'items' | 'onChange' | 'value' | 'variant'
  > & { 'data-testid'?: string };
};

const ItemsSelectionDialog = <T extends Item | string = string>({
  className,
  formDialogProps,
  getTextOnTopOfSelectedList,
  isVisible,
  selectionComponent = 'select',
  onClose,
  onSave,
  items,
  preSelectedItems = [],
  searchInputProps = {},
  SelectedListItem,
  selectProps = {},
  loading,
  saveButtonAriaLabel,
  onSelectedItemsChange,
}: ItemsSelectionDialogProps<T>) => {
  const [selectedItems, setSelectedItems] = useState<T[]>(preSelectedItems);
  const [searchInputText, setSearchInputText] = useState('');
  const [searchInputValue, setSearchInputValue] = useState();

  const classes = useStyles();

  const selectableItems = useMemo(
    () => items.filter((t) => !selectedItems.includes(t)),
    [items, selectedItems]
  );

  useEffect(() => {
    if (onSelectedItemsChange) {
      onSelectedItemsChange(selectedItems);
    }
  }, [selectedItems, onSelectedItemsChange]);

  const handleItemSelected = useCallback<
    ComponentProps<typeof SPWBaseSelect>['onChange']
  >(
    (item) => {
      if (item) {
        setSearchInputValue(undefined);
        setSearchInputText('');

        setSelectedItems((prevValue) => [...prevValue, item as T]);
      }
    },
    [setSearchInputText, setSelectedItems, setSearchInputValue]
  );

  const handleRemove = useCallback(
    (item: Item | string) => {
      setSelectedItems((prevValue) => prevValue.filter((t) => t !== item));
    },
    [setSelectedItems]
  );

  const handleSave = useCallback(() => {
    onSave(selectedItems);
    setSelectedItems([]);
  }, [onSave, selectedItems]);

  const handleClose = useCallback(() => {
    onClose();
    setSelectedItems([]);
  }, [onClose]);

  const handleSearchInputTextDelete = useCallback(() => {
    setSearchInputText('');
  }, [setSearchInputText]);

  const handleSearchInputTextChange = useCallback(
    (
      event: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      if (reason === 'reset') {
        setSearchInputText('');
      } else {
        setSearchInputText(value);
      }
    },
    [setSearchInputText]
  );

  return (
    <SPWFormDialog
      {...formDialogProps}
      className={`${className ?? ''} ${classes.form}`}
      open={isVisible}
      onClose={handleClose}
      disableScrollLock
      dialogActions={
        <>
          <SPWSaveButton
            aria-label={saveButtonAriaLabel}
            className={classes.saveButton}
            disabled={!selectedItems.length}
            onClick={handleSave}
          />
          <SPWCancelButton customColor={colors.black} onClick={handleClose} />
        </>
      }
    >
      {selectionComponent === 'search-input' ? (
        <SPWSearchInput
          {...searchInputProps}
          className={`${searchInputProps?.className ?? ''} ${
            classes.searchInput
          }`}
          inputValue={searchInputText}
          options={selectableItems}
          onChange={handleItemSelected}
          onDelete={handleSearchInputTextDelete}
          onInputChange={handleSearchInputTextChange}
          value={searchInputValue}
          loading={loading}
        />
      ) : (
        <SPWBaseSelect<T>
          {...selectProps}
          fullWidth
          items={selectableItems}
          onChange={handleItemSelected}
          value=""
          variant="outlined"
        />
      )}
      {!!selectedItems.length && (
        <>
          <small className={classes.small}>
            {getTextOnTopOfSelectedList(selectedItems)}
          </small>
          <ul className={classes.selectedItemsList}>
            {selectedItems.map((item) => (
              <li key={getIdentity(item)}>
                <Row alignItems="center" justifyContent="space-between">
                  <SelectedListItem item={item} />
                  <SPWIconButton onClick={() => handleRemove(item)}>
                    <Img src={DeleteIcon} alt="remove item" />
                  </SPWIconButton>
                </Row>
              </li>
            ))}
          </ul>
        </>
      )}
    </SPWFormDialog>
  );
};

export default ItemsSelectionDialog;
